// Shared bracket data helpers used by the bracket page, the mobile pager and
// the XLSX/PDF exports. Keep this file free of React and DOM dependencies.

import { GameStatus } from '../constants/gameConstants';
import { buildBracketLayout, roundNumberOf } from './bracketLayout';
import {
  findScheduleEntryForBracketRound,
  formatScheduleDateTime
} from './roundSchedule';

const PLACEHOLDER_PREFIXES = ['tbd-', 'section-', 'winner-of-'];

export const isPlaceholderParticipant = (participant) => {
  if (!participant) {
    return true;
  }

  const id = String(participant.id || '');
  if (!id || PLACEHOLDER_PREFIXES.some((prefix) => id.startsWith(prefix))) {
    return true;
  }

  return participant.name === 'TBD' || participant.name === 'Walk Over';
};

export const getMatchParties = (match) => {
  const participants = Array.isArray(match?.participants) ? match.participants : [];
  const isWalkOver = match?.state === 'WALK_OVER';
  const missingName = isWalkOver ? 'Walk Over' : 'TBD';
  const topMissing = !participants[0];
  const bottomMissing = !participants[1];
  const topParty = participants[0] || { id: `tbd-${match?.id || 'match'}-1`, name: missingName };
  const bottomParty = participants[1] || { id: `tbd-${match?.id || 'match'}-2`, name: missingName };

  return {
    participants,
    isWalkOver,
    missingName,
    topMissing,
    bottomMissing,
    topParty,
    bottomParty,
    hasRealParticipants:
      participants.length >= 2 &&
      !isPlaceholderParticipant(topParty) &&
      !isPlaceholderParticipant(bottomParty)
  };
};

export const getParticipantName = (party, match) => {
  if (party?.name) {
    return party.name;
  }
  return match?.state === 'WALK_OVER' ? 'Walk Over' : 'TBD';
};

export const getParticipantStatusLabel = (party) => {
  if (party?.status === 'WALK_OVER') return 'WO';
  if (party?.status === 'NO_SHOW') return 'NS';
  return '';
};

export const getMatchWinner = (match) => {
  const participants = Array.isArray(match?.participants) ? match.participants : [];
  return participants.find((participant) => participant?.isWinner) || null;
};

export const getMatchGame = (games, matchId) =>
  (Array.isArray(games) ? games : []).find((game) => game.match === matchId) || null;

export const getMatchStatusLabel = (match, game) => {
  if (match?.state === 'WALK_OVER') return 'Walk Over';
  if (match?.state === 'NO_SHOW') return 'No Show';
  if (game?.status === GameStatus.IN_PROGRESS) return 'In Progress';
  if (game?.status === GameStatus.COMPLETE) return 'Complete';
  if (match?.state === 'DONE') return 'Complete';
  return 'Pending';
};

// Where the draw sheet places a match, e.g. "Durbanville · Green A · Rink 3".
// venueName is kept on the match even when the venue is not one of the event's
// locations, so the node still shows what the sheet said.
export const getMatchLocationLabel = (match) => {
  const parts = [];
  const venue = String(match?.venueName || '').trim();
  const green = String(match?.green || '').trim();
  const rink = match?.rink;

  if (venue) parts.push(venue);
  if (green) parts.push(green);
  if (rink !== null && rink !== undefined && String(rink).trim() !== '') {
    parts.push(`Rink ${rink}`);
  }

  return parts.join(' · ');
};

const scoreValue = (value) => (value === 0 || value ? String(value) : '');

export const getMatchScore = (match, game) => {
  if (!game) {
    return { top: '', bottom: '', text: '' };
  }

  const { topParty, bottomParty } = getMatchParties(match);
  let top = '';
  let bottom = '';

  if (game.status === GameStatus.COMPLETE) {
    top = scoreValue(game.teamTotals?.[topParty.id]);
    bottom = scoreValue(game.teamTotals?.[bottomParty.id]);
  } else if (game.status === GameStatus.IN_PROGRESS) {
    const currentSet = (game.sets || []).find((set) => set.id === game.currentSet) || game.currentSet;
    top = scoreValue(currentSet?.totalSetScore?.[topParty.id]);
    bottom = scoreValue(currentSet?.totalSetScore?.[bottomParty.id]);
  }

  return {
    top,
    bottom,
    text: top !== '' && bottom !== '' ? `${top}-${bottom}` : ''
  };
};

export const groupMatchesByRound = (matches) => {
  const rounds = new Map();

  (Array.isArray(matches) ? matches : []).forEach((match) => {
    const round = roundNumberOf(match);
    if (!rounds.has(round)) {
      rounds.set(round, []);
    }
    rounds.get(round).push(match);
  });

  return Array.from(rounds.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([round, roundMatches]) => ({ round, matches: roundMatches }));
};

/**
 * Rounds in order, and each round's matches top-to-bottom. Positions come from
 * the tree layout when supplied; otherwise a topology-only layout (0-height
 * cards) is enough to order them without depending on rendered sizes.
 */
export const getOrderedRounds = (matches, positions = null) => {
  const list = Array.isArray(matches) ? matches : [];
  if (!list.length) {
    return [];
  }

  const layoutPositions =
    positions || buildBracketLayout({ matches: list, cardHeight: 0, gap: 1 }).positions;
  const indexById = new Map(list.map((match, index) => [match.id, index]));

  return groupMatchesByRound(list).map(({ round, matches: roundMatches }) => ({
    round,
    matches: roundMatches.slice().sort((a, b) => {
      const positionA = layoutPositions?.[a.id];
      const positionB = layoutPositions?.[b.id];

      if (positionA != null && positionB != null && positionA !== positionB) {
        return positionA - positionB;
      }
      if (positionA != null && positionB == null) return -1;
      if (positionB != null && positionA == null) return 1;
      return (indexById.get(a.id) ?? 0) - (indexById.get(b.id) ?? 0);
    })
  }));
};

export const getRoundTitle = (round, matches) => {
  const first = Array.isArray(matches) ? matches[0] : null;
  if (!first) {
    return `Round ${round}`;
  }
  return first.roundName || (first.name ? String(first.name).split(' - ')[0] : `Round ${round}`);
};

// One bracket per event is the convention. If a stale event ever has more,
// the page and the export must still pick the same one.
export const pickPrimaryBracket = (brackets) => {
  const list = (Array.isArray(brackets) ? brackets : []).filter(Boolean);
  if (!list.length) {
    return null;
  }

  return list.slice().sort((a, b) => {
    const nameA = String(a.name || '').toLowerCase();
    const nameB = String(b.name || '').toLowerCase();
    return nameA === nameB
      ? String(a.id || '').localeCompare(String(b.id || ''))
      : nameA.localeCompare(nameB);
  })[0];
};

export const buildBracketRows = (bracket, games, roundSchedule = []) => {
  const matches = Array.isArray(bracket?.matches) ? bracket.matches : [];
  if (!matches.length) {
    return [];
  }

  const gameByMatch = new Map(
    (Array.isArray(games) ? games : [])
      .filter((game) => game?.match)
      .map((game) => [game.match, game])
  );

  const rows = [
    ['Round', 'Match', 'Team 1', 'Team 2', 'Location', 'Date/Time', 'Winner', 'Status']
  ];

  getOrderedRounds(matches).forEach(({ round, matches: roundMatches }) => {
    const roundTitle = getRoundTitle(round, roundMatches);
    const dateTime = formatScheduleDateTime(
      findScheduleEntryForBracketRound(roundSchedule, roundTitle)
    );

    roundMatches.forEach((match) => {
      const parties = getMatchParties(match);
      const game = gameByMatch.get(match.id) || null;
      rows.push([
        roundTitle,
        match.name || '',
        getParticipantName(parties.topParty, match),
        getParticipantName(parties.bottomParty, match),
        getMatchLocationLabel(match),
        dateTime,
        getMatchWinner(match)?.name || '',
        getMatchStatusLabel(match, game)
      ]);
    });
  });

  return rows;
};
